// `/focus/meetings/import?project=<id>`: process a Meet meeting
// the owner organized and recorded WITHOUT Loom, for the page's project.
//
// - Reached from one project's meetings page ("Schon aufgenommen?"), so the
//   project is the URL's; there is no project step (owner decision: an
//   imported meeting always has a project). Without one: "Wähle zuerst ein
//   Projekt". With the feature off: "not found", like its siblings.
// - Google not connected: one line to the account page, nothing else (the
//   only place to connect). A read or an import that finds the grant dead
//   (`host_lost`) or gone (`google_disconnected`) says so with "Verbinde
//   Google neu", a link to the account page too (its Google row offers
//   "Neu verbinden"). The shared `error.host_lost` line is `/m`'s, with the
//   public name and no link: a link holder has no account page.
// - The list (`GET /api/meetings/importable`) shows recorded meetings only,
//   grouped by day, each with its state from the server ("Aufnahme bereit",
//   "Aufnahme wird erstellt", "Läuft noch", "Zu alt zum Verarbeiten",
//   "Schon verarbeitet" → open) and "Noch N Tage" when Google deletes it
//   within a week.
// - "Meet-Link oder Code einfügen": parsed here with the shared parser
//   (`parseMeetInput`, no regex lookbehind). A code the list has marks its
//   rows and selects the newest; otherwise the server is asked for that
//   code's meetings, which then show on their own. Nothing found is one
//   sentence naming the connected Google account, plus one more when the
//   link's `authuser` is another account.
// - Picking a row shows one confirm line naming the project; "Verarbeiten"
//   sends the row's newest conference (the server rebuilds the meeting from
//   Google itself) and replaces this page with the meeting's page. The copy
//   promises no immediate work: processing runs on the next job tick.

import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import {
  parseMeetInput,
  type MeetingImportableDto,
  type MeetingImportableRowDto,
} from "@loom/shared/meetings";

import { AccountNavLink } from "../../account/AccountNavLink";
import { useFormatDate } from "../../i18n/format";
import { usePlural, useT, type MessageKey } from "../../i18n/index.js";
import { dayKeyOf, groupByDay } from "../../lib/group-by-day";
import { RouteNotFound } from "../../lib/RouteError";
import { ThemeToggle } from "../../theme/ThemeToggle";
import { FocusFrame, Headline, Help, Kicker, ThinkingDots } from "../ui";

import { failureOf, importMeeting, listImportable, type MeetingFailure } from "./meetingApi";
import { failureKeyOf } from "./meetingCopy";
import { meetingsImportHref } from "./MeetingsEntry";
import { useProjectName } from "./MeetingsPage";
import { useMeetingsStatus } from "./useMeetings";

const TIME_FORMAT: Intl.DateTimeFormatOptions = { timeStyle: "short" };
const DAY_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };
const DAY_MS = 86_400_000;
/** "Noch N Tage" shows once Google deletes the meeting within this many days. */
export const DAYS_LEFT_SHOWN = 7;
/** An example in the paste field: a link's shape, the same in every language. */
const PASTE_PLACEHOLDER = "meet.google.com/abc-defg-hij";

/** A row Loom can take now (the server refuses the others too). */
const selectable = (row: MeetingImportableRowDto): boolean =>
  row.state === "ready" || row.state === "generating";

/** The line for a dead (`host_lost`) or missing Google grant, else null. */
const googleFailureKeyOf = (f: MeetingFailure): MessageKey | null =>
  f.code === "host_lost"
    ? "meetings.import.googleLost"
    : f.code === "google_disconnected"
      ? "meetings.import.googleGone"
      : null;

/** The lines whose fix is the account page's Google row. */
const GOOGLE_FAILURE_KEYS: ReadonlySet<MessageKey> = new Set<MessageKey>([
  "meetings.import.googleLost",
  "meetings.import.googleGone",
]);
const needsGoogle = (key: MessageKey): boolean => GOOGLE_FAILURE_KEYS.has(key);

/** What a failed import says: the import's own 404, a Google grant to fix,
 *  else the meetings catalog. */
const importFailureKey = (f: MeetingFailure): MessageKey =>
  f.status === 404
    ? "meetings.error.importNotFound"
    : (googleFailureKeyOf(f) ?? failureKeyOf(f));

/** The days until Google deletes the record, rounded up; null beyond a week. */
const daysLeftOf = (row: MeetingImportableRowDto, nowMs: number): number | null => {
  const days = Math.ceil((Date.parse(row.expiresAt) - nowMs) / DAY_MS);
  return days <= DAYS_LEFT_SHOWN ? Math.max(days, 1) : null;
};

function ImportRow({
  row,
  selected,
  marked,
  onSelect,
  onOpenMeeting,
}: {
  readonly row: MeetingImportableRowDto;
  readonly selected: boolean;
  readonly marked: boolean;
  readonly onSelect: () => void;
  readonly onOpenMeeting: (meetingId: string) => void;
}) {
  const t = useT();
  const tn = usePlural();
  const time = useFormatDate(TIME_FORMAT);
  const title =
    row.title ?? (row.meetingCode !== null ? `Meet ${row.meetingCode}` : t("meetings.row.untitled"));
  const minutes = row.durationS === null ? null : Math.max(1, Math.round(row.durationS / 60));
  const daysLeft = selectable(row) ? daysLeftOf(row, Date.now()) : null;
  const head = (
    <>
      <span className="meet-row-head">
        <span className="meet-meta">
          {time(row.startedAt)}
          {minutes !== null && ` · ${t("meetings.import.duration", { minutes })}`}
        </span>
        <span className="meet-row-title">{title}</span>
      </span>
      <span className="meet-row-head">
        <span className={`focus-chip meet-import-state is-${row.state}`}>
          {t(`meetings.import.state.${row.state}` as MessageKey)}
        </span>
        {daysLeft !== null && (
          <span className="focus-chip">{tn("plural.meetingDaysLeft", daysLeft)}</span>
        )}
        {row.state === "running" && (
          <span className="meet-meta">{t("meetings.import.runningHelp")}</span>
        )}
        {selected && <span className="focus-chip is-on">{t("meetings.import.selected")}</span>}
      </span>
    </>
  );
  const className = `meet-row${marked ? " is-marked" : ""}${selected ? " is-selected" : ""}`;
  if (row.state === "imported" && row.meetingId !== null) {
    const meetingId = row.meetingId;
    return (
      <li>
        <div className={className}>
          {head}
          <div className="meet-actions">
            <button type="button" className="focus-btn is-ghost" onClick={() => onOpenMeeting(meetingId)}>
              {t("meetings.import.open")}
            </button>
          </div>
        </div>
      </li>
    );
  }
  if (!selectable(row)) {
    return (
      <li>
        <div className={`${className} is-disabled`} aria-disabled="true">
          {head}
        </div>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        className={`${className} is-button`}
        aria-pressed={selected}
        onClick={onSelect}
      >
        {head}
      </button>
    </li>
  );
}

export function MeetingImportPage({
  projectId,
  onBack,
  onOpenMeeting,
}: {
  /** The project the URL names; null without one (nothing to import then). */
  readonly projectId: number | null;
  /** To the project's meetings page. */
  readonly onBack: () => void;
  /** To one meeting's page; `replace` after an import (Back skips this page). */
  readonly onOpenMeeting: (meetingId: string, opts?: { readonly replace?: boolean }) => void;
}) {
  const t = useT();
  const day = useFormatDate(DAY_FORMAT);
  const { status } = useMeetingsStatus();
  const enabled = status?.enabled === true;
  const connected = enabled && status?.googleConnected === true;
  const projectName = useProjectName(enabled, projectId, null);

  const [listed, setListed] = useState<MeetingImportableDto | null>(null);
  const [loadError, setLoadError] = useState<MessageKey | null>(null);
  const [reload, setReload] = useState(0);
  const [paste, setPaste] = useState("");
  const [pasteError, setPasteError] = useState<MessageKey | null>(null);
  /** A pasted code: its rows are marked (from the list) or shown alone (from the server). */
  const [code, setCode] = useState<string | null>(null);
  const [authuser, setAuthuser] = useState<string | null>(null);
  const [codeAnswer, setCodeAnswer] = useState<MeetingImportableDto | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<MessageKey | null>(null);
  // The "Verarbeiten" box sits under the whole list: a pick far up the page
  // brings it into view.
  const confirmRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = confirmRef.current;
    if (selected === null || box === null) return;
    if (typeof box.scrollIntoView === "function") box.scrollIntoView({ block: "nearest" });
  }, [selected]);

  useEffect(() => {
    if (!connected || projectId === null) return undefined;
    let alive = true;
    setLoadError(null);
    listImportable()
      .then((next) => {
        if (alive) setListed(next);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(failureKeyOfList(failureOf(err)));
      });
    return () => {
      alive = false;
    };
  }, [connected, projectId, reload]);

  const rows = useMemo(() => {
    if (codeAnswer !== null) return codeAnswer.rows;
    return listed?.rows ?? null;
  }, [codeAnswer, listed]);

  if (status === null) {
    return (
      <FocusFrame stepKey="meetings-import-loading" onBack={onBack}>
        <ThinkingDots label={t("common.loading")} />
      </FocusFrame>
    );
  }
  if (!enabled) return <RouteNotFound />;

  const here = projectId === null ? undefined : meetingsImportHref(projectId);
  const chrome = (
    <>
      <AccountNavLink from={here} />
      <ThemeToggle />
    </>
  );

  if (projectId === null) {
    return (
      <FocusFrame stepKey="meetings-import-no-project" onBack={onBack} chromeAside={chrome}>
        <Kicker>{t("meetings.kicker")}</Kicker>
        <Headline>{t("meetings.noProject.headline")}</Headline>
        <div className="meet-actions">
          <button type="button" className="focus-btn" onClick={onBack}>
            {t("meetings.noProject.toProjects")}
          </button>
        </div>
      </FocusFrame>
    );
  }

  if (!connected) {
    return (
      <FocusFrame stepKey="meetings-import" onBack={onBack} chromeAside={chrome}>
        <Kicker>{t("meetings.kicker")}</Kicker>
        <Headline>{t("meetings.import.headline")}</Headline>
        <p className="focus-help">
          <AccountNavLink label="meetings.google.connectInAccount" from={here} />
        </p>
      </FocusFrame>
    );
  }

  const googleEmail = codeAnswer?.googleEmail ?? listed?.googleEmail ?? null;

  const find = (e: FormEvent): void => {
    e.preventDefault();
    setPasteError(null);
    setSubmitError(null);
    const parsed = parseMeetInput(paste);
    if (parsed === null) {
      setPasteError("meetings.import.pasteInvalid");
      return;
    }
    setCode(parsed.code);
    setAuthuser(parsed.authuser);
    const known = (listed?.rows ?? []).filter((r) => r.meetingCode === parsed.code);
    if (known.length > 0) {
      setCodeAnswer(null);
      setSelected(known.find(selectable)?.anchorConference ?? null);
      return;
    }
    setSearching(true);
    listImportable(parsed.code)
      .then((answer) => {
        setCodeAnswer(answer);
        setSelected(answer.rows.find(selectable)?.anchorConference ?? null);
      })
      .catch((err: unknown) => setPasteError(failureKeyOfList(failureOf(err))))
      .finally(() => setSearching(false));
  };

  const clearCode = (): void => {
    setCode(null);
    setAuthuser(null);
    setCodeAnswer(null);
    setPaste("");
    setPasteError(null);
  };

  const submit = (): void => {
    if (selected === null || submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    importMeeting(selected, projectId)
      .then((res) => onOpenMeeting(res.meetingId, { replace: true }))
      .catch((err: unknown) => {
        setSubmitError(importFailureKey(failureOf(err)));
        setSubmitting(false);
      });
  };

  const todayKey = dayKeyOf(new Date().toISOString());
  const yesterdayKey = dayKeyOf(new Date(Date.now() - DAY_MS).toISOString());
  const dayHeading = (key: string): string =>
    key === todayKey
      ? t("meetings.list.today")
      : key === yesterdayKey
        ? t("meetings.list.yesterday")
        : day(`${key}T12:00:00`);
  const groups = groupByDay(rows ?? [], (r) => r.startedAt);
  const selectedRow = (rows ?? []).find((r) => r.anchorConference === selected) ?? null;
  // The link was for another Google account than the connected one.
  const mismatch =
    authuser !== null &&
    authuser.includes("@") &&
    googleEmail !== null &&
    authuser.trim().toLowerCase() !== googleEmail.trim().toLowerCase();
  const nothingForCode = codeAnswer !== null && codeAnswer.rows.length === 0;

  return (
    <FocusFrame stepKey="meetings-import" onBack={onBack} wide chromeAside={chrome}>
      <Kicker>{t("meetings.kicker")}</Kicker>
      <Headline>{t("meetings.import.headline")}</Headline>
      {projectName !== null && (
        <p className="meet-meta meet-scope">{t("meetings.project.current", { project: projectName })}</p>
      )}
      <Help>{t("meetings.import.help")}</Help>

      <form className="meet-paste" onSubmit={find}>
        <label className="meet-edit-field">
          <span className="meet-edit-label">{t("meetings.import.pasteLabel")}</span>
          <input
            className="meet-edit-input"
            type="text"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder={PASTE_PLACEHOLDER}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
          />
        </label>
        <div className="meet-actions">
          <button type="submit" className="focus-btn is-ghost" disabled={searching || paste.trim() === ""}>
            {t("meetings.import.find")}
          </button>
          {code !== null && (
            <button type="button" className="focus-deeplink" onClick={clearCode}>
              {t("meetings.import.showAll")}
            </button>
          )}
        </div>
        {pasteError !== null && (
          <div className="focus-error" role="alert">
            <FailureText messageKey={pasteError} from={here} />
          </div>
        )}
      </form>

      {searching && <ThinkingDots label={t("meetings.import.loading")} />}
      {nothingForCode && (
        <p className="focus-help" role="status">
          {googleEmail !== null
            ? t("meetings.import.none", { email: googleEmail })
            : t("meetings.import.noneNoEmail")}
        </p>
      )}
      {mismatch && (nothingForCode || code !== null) && (
        <p className="focus-help">
          {t("meetings.import.accountMismatch", { link: authuser ?? "", email: googleEmail ?? "" })}
        </p>
      )}

      {rows === null && loadError === null && <ThinkingDots label={t("meetings.import.loading")} />}
      {loadError !== null && (
        <div className="focus-error" role="alert">
          {needsGoogle(loadError) ? (
            <FailureText messageKey={loadError} from={here} />
          ) : (
            <>
              {t(loadError)}{" "}
              <button type="button" className="focus-deeplink" onClick={() => setReload((n) => n + 1)}>
                {t("common.retry")}
              </button>
            </>
          )}
        </div>
      )}
      {rows !== null && rows.length === 0 && codeAnswer === null && <Help>{t("meetings.import.empty")}</Help>}

      {groups.map((g) => (
        <section key={g.key} className="meet-section">
          <h2 className="meet-section-title">{dayHeading(g.key)}</h2>
          <ul className="meet-list">
            {g.entries.map((row) => (
              <ImportRow
                key={row.anchorConference}
                row={row}
                selected={row.anchorConference === selected}
                marked={code !== null && row.meetingCode === code}
                onSelect={() => {
                  setSelected(row.anchorConference);
                  setSubmitError(null);
                }}
                onOpenMeeting={(meetingId) => onOpenMeeting(meetingId)}
              />
            ))}
          </ul>
        </section>
      ))}

      {selectedRow !== null && (
        <div ref={confirmRef} className="meet-confirm meet-import-confirm">
          <p className="meet-summary">
            {projectName !== null
              ? t("meetings.import.confirm", { project: projectName })
              : t("meetings.import.confirmThisProject")}
          </p>
          <div className="meet-actions">
            <button type="button" className="focus-btn" disabled={submitting} onClick={submit}>
              {t("meetings.import.submit")}
            </button>
          </div>
          {submitting && <ThinkingDots label={t("meetings.import.submitting")} />}
          {submitError !== null && (
            <div className="focus-error" role="alert">
              <FailureText messageKey={submitError} from={here} />
            </div>
          )}
        </div>
      )}
    </FocusFrame>
  );
}

/** A failure line; a Google grant to fix adds "Verbinde Google neu", the
 *  link to the account page (this page's URL as its Back). */
function FailureText({
  messageKey,
  from,
}: {
  readonly messageKey: MessageKey;
  readonly from: string | undefined;
}) {
  const t = useT();
  if (!needsGoogle(messageKey)) return <>{t(messageKey)}</>;
  return (
    <>
      {t(messageKey)} <AccountNavLink label="meetings.import.reconnectGoogle" from={from} />
    </>
  );
}

/** A failed list read: Google's own codes, else "couldn't read". */
const failureKeyOfList = (f: MeetingFailure): MessageKey => {
  if (f.code === "invalid_code") return "meetings.import.pasteInvalid";
  return googleFailureKeyOf(f) ?? "meetings.import.loadFailed";
};
